import { useCallback, useEffect, useRef, useState } from 'react'
import AddStaffForm from '../components/AddStaffForm'
import ConfirmDialog from '../components/ConfirmDialog'
import InviteComposer from '../components/InviteComposer'
import PasteStaffForm from '../components/PasteStaffForm'
import TalkToCathy from '../components/TalkToCathy'
import { api } from '../lib/api'
import { formatShortDate } from '../lib/format'
import { principalView, variablePrefill } from '../lib/variables'

// The principal's surface. /discovery is her home; /discovery/<space_id> is
// one space as she sees it, laid out after the "one-click send" proposal: a
// dark band that says the one thing to do — send — then the people it goes
// to on the left, the email itself in the middle (editable in place, shown
// as the selected person will receive it) with the one button under it, and
// what happens after on the right. Once everything is sent the band says
// nothing else is needed, and the page becomes the tracker: each person's
// row carries their state, with Reissue for a link that has closed. No
// Revoke here: an email that has gone out can't be recalled, and the admin
// keeps that lever on the roster for the rare case.
//
// Nothing here shows a transcript or a write-up: the API she calls has none
// to give (CLAUDE.md § Access Control). Built for a phone first: the email
// and its button come before the list there.
export default function DiscoveryPage({ route, user, onForward, onOpen, onSpaces = null }) {
  if (!route.spaceId) {
    return <DiscoveryHome user={user} onForward={onForward} onOpen={onOpen} onSpaces={onSpaces} />
  }
  return <PrincipalSpace spaceId={route.spaceId} user={user} />
}

// The principal's home: her spaces, from GET /spaces/mine. One space is the
// usual case and she never sees a list for it — the page forwards straight
// there. Several are listed; none is an honest empty state. An admin who
// lands here has none as principal and is pointed back to Spaces.
function DiscoveryHome({ user, onForward, onOpen, onSpaces }) {
  const [spaces, setSpaces] = useState(null)
  const [error, setError] = useState(null)
  // The shell hands a fresh callback on every render; the load must not
  // re-run for that, only on mount and on "Try again".
  const forward = useRef(onForward)
  forward.current = onForward

  const load = useCallback(() => {
    setError(null)
    api('/api/v1/spaces/mine')
      .then((rows) => {
        if (rows.length === 1) forward.current(rows[0].id)
        else setSpaces(rows)
      })
      .catch((err) => setError(err.detail || 'Could not load your spaces.'))
  }, [])

  useEffect(load, [load])

  const shell = (children) => (
    <main
      className="w-full px-4 md:w-[85%] md:mx-auto md:px-0"
      style={{ paddingTop: 'var(--space-l)', paddingBottom: 'var(--space-2xl)' }}
    >
      {children}
    </main>
  )

  if (error) {
    return shell(
      <div className="alert alert--error" role="alert">
        <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
          <p className="text-body-md">{error}</p>
          <div>
            <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>,
    )
  }

  if (spaces === null) {
    return shell(
      <div className="card" role="status">
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
          Loading your spaces…
        </p>
      </div>,
    )
  }

  if (spaces.length === 0) {
    return shell(
      <div
        className="card"
        style={{ textAlign: 'center', paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-xl)' }}
      >
        <h2 className="text-h4" style={{ marginBottom: 'var(--space-xs)' }}>
          Nothing has been shared with you yet
        </h2>
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
          When an engagement is shared with you, the personal link in that email opens it here.
        </p>
        {onSpaces && (
          <div style={{ marginTop: 'var(--space-s)' }}>
            <button type="button" className="btn btn--secondary btn--sm" onClick={onSpaces}>
              All spaces
            </button>
          </div>
        )}
      </div>,
    )
  }

  return shell(
    <>
      <p className="overline">Your spaces</p>
      <h2 className="text-h3" style={{ marginBottom: 'var(--space-m)' }}>
        Welcome, {user.name}
      </h2>
      <ul className="space-cards">
        {spaces.map((space) => (
          <li key={space.id}>
            <button type="button" className="space-card-btn" onClick={() => onOpen(space.id)}>
              <span className="space-card-btn__client">{space.client_name}</span>
              <span className="space-card-btn__title">{space.engagement}</span>
              <span className="space-card-btn__meta">
                {space.staff_count} {space.staff_count === 1 ? 'person' : 'people'} ·{' '}
                {space.completed_count} done · ends {formatShortDate(space.ends_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>,
  )
}

// The onboarding voice agent's name, as the welcome band introduces her. One
// place to change it; the agent itself is TalkToCathy.jsx.
const AGENT_NAME = 'Cathy'

// The strip under the band: what happens once she presses Send, in three.
const WHAT = [
  {
    title: 'They talk for 20–30 minutes',
    body: '',
  },
  {
    title: 'Capston reviews the summary',
    body: '',
  },
  {
    title: 'Discuss the findings at the lab',
    body: '',
  },
]

function PrincipalSpace({ spaceId, user }) {
  const [space, setSpace] = useState(null)
  const [state, setState] = useState('loading') // loading | ready | missing | error
  const [error, setError] = useState(null)
  const [flash, setFlash] = useState(null)
  const [selected, setSelected] = useState(null) // a link id, or a participant id
  // On a wide screen the first person is previewed from the start — the
  // two columns make the link obvious. On a phone the email shows the
  // generic version until a name is tapped; a preselected person would
  // read as the page choosing for them. Read once, on load.
  const [wide] = useState(() => window.matchMedia('(min-width: 1024px)').matches)
  const [adding, setAdding] = useState(false) // false | 'one' | 'many'
  const [busy, setBusy] = useState(null) // 'send-all' | a row key
  const [sentMsg, setSentMsg] = useState(null)
  const [editing, setEditing] = useState(false) // the composer has an unsaved edit

  // The "Sent to …" line clears itself, so the phone's bottom bar — which
  // stays up only to show it once everything is sent — goes away too.
  useEffect(() => {
    if (!sentMsg) return undefined
    const timer = setTimeout(() => setSentMsg(null), 8000)
    return () => clearTimeout(timer)
  }, [sentMsg])

  const load = useCallback(() => {
    // A reload after an action keeps the page up rather than flashing the
    // loading card; only the first load, or a retry after an error, shows it.
    setState((current) => (current === 'ready' ? current : 'loading'))
    setError(null)
    return api(`/api/v1/spaces/${spaceId}`)
      .then((loaded) => {
        setSpace(principalView(loaded))
        setState('ready')
      })
      .catch((err) => {
        // A space that is not hers, has ended, or never existed is one 404 —
        // the page says the same thing for all of them.
        if (err.status === 404) {
          setState('missing')
        } else {
          setError(err.detail || 'Could not load this space.')
          setState('error')
        }
      })
  }, [spaceId])

  // `load` returns its promise so actions can await the refresh; an effect
  // must not return that, or React calls it as the cleanup.
  useEffect(() => {
    load()
  }, [load])

  const shell = (children) => (
    <main
      className="w-full px-4 md:w-[85%] md:mx-auto md:px-0"
      style={{ paddingTop: 'var(--space-l)', paddingBottom: 'var(--space-2xl)' }}
    >
      {children}
    </main>
  )

  if (state === 'loading') {
    return shell(
      <div className="card" role="status">
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
          Loading your space…
        </p>
      </div>,
    )
  }

  if (state === 'missing') {
    return shell(
      <div
        className="card"
        style={{ textAlign: 'center', paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-xl)' }}
      >
        <h2 className="text-h4" style={{ marginBottom: 'var(--space-xs)' }}>
          This space isn&rsquo;t available
        </h2>
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
          The engagement may have ended, or this link may not be yours. If you think that&rsquo;s
          wrong, reply to the email that shared it with you.
        </p>
      </div>,
    )
  }

  if (state === 'error') {
    return shell(
      <div className="alert alert--error" role="alert">
        <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
          <p className="text-body-md">{error}</p>
          <div>
            <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>,
    )
  }

  // Her own interview link sits on the roster like anyone else's (the
  // onboarding note carries it), but she is not one of the people she is
  // inviting, so this list leaves her out. The admin's roster keeps her.
  const mine = (space.principal_email || '').trim().toLowerCase()
  const rows = (space.staff ?? []).filter((row) => (row.email || '').trim().toLowerCase() !== mine)
  const key = (row) => row.link_id ?? row.participant_id
  const name = (row) => `${row.first_name} ${row.last_name}`
  const unsent = rows.filter((row) => row.link_id && row.status === 'not_sent')
  const sentRows = rows.filter((row) => row.link_id && row.status !== 'not_sent')
  // Someone who has finished needs nothing more from her: they sit at the
  // bottom, out of the way of the people still to reach.
  const open = rows.filter((row) => row.status !== 'completed')
  const done = rows.filter((row) => row.status === 'completed')
  const ordered = [...open, ...done]
  // Live = still taking sends. The server refuses everything once it has
  // ended or been archived; the page says so instead of offering buttons.
  const live = !space.archived_at && daysLeft(space.ends_at) >= 0
  const first = (space.principal_name || user.name || '').split(/\s+/)[0]
  const person = ordered.find((row) => key(row) === selected) ?? (wide ? ordered[0] : null) ?? null
  const links = `/api/v1/spaces/${space.id}/links`

  // One click sends to everyone not yet sent — no picking. Anyone added
  // later joins the next send the same way.
  async function sendAll() {
    if (unsent.length === 0) return
    setBusy('send-all')
    setFlash(null)
    setSentMsg(null)
    const failed = []
    for (const row of unsent) {
      try {
        await api(`${links}/${row.link_id}/send`, { method: 'POST' })
      } catch {
        failed.push(name(row))
      }
    }
    await load()
    if (failed.length > 0) {
      const sent = unsent.length - failed.length
      setFlash({
        type: 'error',
        text: `Sent ${sent} of ${unsent.length}. Could not send to ${failed.join(', ')} — try again in a moment.`,
      })
    } else {
      setSentMsg(
        `Sent to ${unsent.map((row) => row.first_name).join(', ')}. Progress shows on each person as they open and finish.`,
      )
    }
    setBusy(null)
  }

  // The Send button names who it goes to.
  const firsts = unsent.map((row) => row.first_name)
  const sendLabel =
    firsts.length <= 2 ? `Send to ${firsts.join(' and ')}` : `Send to ${firsts.length} people`
  const sendNote = sentMsg
    ? { text: sentMsg, tone: 'ok' }
    : { text: `Sent in your name · replies come to ${space.principal_email}`, tone: null }

  // A fresh link, sent straight away: reissue drafts it, send sends it.
  async function reissue(row) {
    setBusy(key(row))
    setFlash(null)
    try {
      const fresh = await api(`${links}/${row.link_id}/reissue`, { method: 'POST' })
      await api(`${links}/${fresh.id}/send`, { method: 'POST' })
      await load()
      setSelected(fresh.id)
    } catch (err) {
      setFlash({ type: 'error', text: err.detail || `Could not send ${name(row)} a new link.` })
    } finally {
      setBusy(null)
    }
  }

  // The green line under the email once everything is out: how many went,
  // and where they stand when not all of them are simply on their way.
  const sentLine = (() => {
    if (sentRows.length === 0) return null
    const count = (status) => sentRows.filter((row) => row.status === status).length
    const parts = [
      [count('completed'), 'done'],
      [count('opened'), 'opened'],
      [count('sent'), 'waiting'],
      [count('expired'), 'expired'],
      [count('revoked'), 'revoked'],
    ].filter(([n]) => n > 0)
    const total = `${sentRows.length} invitation${sentRows.length === 1 ? '' : 's'} sent`
    if (parts.length === 1 && parts[0][1] === 'waiting') return total
    return `${total} · ${parts.map(([n, word]) => `${n} ${word}`).join(' · ')}`
  })()

  // Under the list heading: where everyone stands, in one line.
  const waiting = rows.filter((row) => ['sent', 'opened'].includes(row.status)).length
  const summaryLine =
    rows.length === 0
      ? null
      : [`${done.length} done`, `${waiting} waiting`, `${unsent.length} not sent`].join(' · ')

  return (
    <main>
      <Band
        space={space}
        first={first}
        live={live}
        voice={
          <TalkToCathy
            agentName={AGENT_NAME}
            name={first}
            org={space.client_name}
            count={unsent.length}
            hint={
              live && unsent.length > 0
                ? 'Questions before you send? Ask her out loud.'
                : 'Questions about how this works? Ask her out loud.'
            }
            variables={{
              engagement: space.engagement,
              purpose: space.purpose || 'not stated',
              sent_count: sentRows.length,
            }}
          />
        }
      />

      <div className={`pr-main${live && rows.length > 0 ? ' pr-main--barred' : ''}`}>
        {flash && (
          <div
            className={`alert alert--${flash.type} text-body-sm pr-main__flash`}
            role={flash.type === 'error' ? 'alert' : 'status'}
          >
            {flash.text}
          </div>
        )}

        {/* Send, first thing under the welcome on a wide screen — between the
            band and the list + email, where it can't be missed. Below 1024px
            the bar pinned to the bottom is the Send (CSS swaps them). */}
        {live && rows.length > 0 && (
          <div className="send-top" role="region" aria-label="Send">
            {unsent.length > 0 ? (
              <>
                <button
                  type="button"
                  className="btn btn--primary btn--xl send-top__btn"
                  onClick={sendAll}
                  disabled={busy === 'send-all' || editing}
                >
                  {busy === 'send-all' ? 'Sending…' : sendLabel}
                </button>
                <p
                  className={`compose__sendnote send-top__note${sendNote.tone ? ` compose__sendnote--${sendNote.tone}` : ''}`}
                  role={sendNote.tone === 'warn' ? 'alert' : 'status'}
                >
                  {editing ? 'Saving your edit…' : sendNote.text}
                </p>
              </>
            ) : (
              <div className="sent-box" role="status">
                {sentMsg ?? sentLine}
              </div>
            )}
          </div>
        )}

        <section className="people" aria-labelledby="people-title">
          <div className="people__head">
            <p id="people-title" className="eyebrow-sm">
              Staff to interview · {rows.length}
            </p>
            {live && (
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding('one')}>
                + Add
              </button>
            )}
          </div>
          {summaryLine ? (
            <p className="people__summary">{summaryLine}</p>
          ) : (
            <p className="people__hint">Nobody has been added yet.</p>
          )}
          <ul className="people__list">
            {/* Open rows first, then everyone who is done, in one list. Any
                row previews its email. */}
            {ordered.map((row) => {
              const on = person && key(person) === key(row)
              const rowBusy = busy === key(row) || busy === 'send-all'
              const closed = row.status === 'revoked' || row.status === 'expired'
              const canReissue = live && closed
              return (
                <li key={key(row)} className={`prow${on ? ' prow--on' : ''}`}>
                  <div
                    className="prow__main"
                    role="button"
                    tabIndex={0}
                    aria-pressed={on}
                    onClick={() => setSelected(key(row))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setSelected(key(row))
                      }
                    }}
                  >
                    <span className="avatar">{initials(row)}</span>
                    <span className="people__text">
                      <span className="people__name">{name(row)}</span>
                      <span className="people__meta">{row.email}</span>
                    </span>
                    {/* Where they stand, top right. The ring already says
                        which row is being previewed. */}
                    <span
                      className={`prow__status${row.status === 'completed' ? ' prow__status--done' : closed ? ' prow__status--closed' : ''}`}
                    >
                      {STATUS_TEXT[row.status] ?? row.label}
                      {rowMeta(row)}
                    </span>
                  </div>

                  {canReissue && (
                    <div className="prow__foot">
                      {canReissue && (
                        <button
                          type="button"
                          className="people__act"
                          onClick={() => reissue(row)}
                          disabled={rowBusy}
                        >
                          {rowBusy ? 'Sending…' : 'Reissue'}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
          {rows.some((row) => ['revoked', 'expired'].includes(row.status)) && (
            <p className="people__hint">Reissue sends that person a fresh link.</p>
          )}
        </section>

        <InviteComposer
          space={space}
          person={person}
          live={live}
          unsentCount={unsent.length}
          onSaved={setSpace}
          onEditingChange={setEditing}
        />

      </div>

      {/* The phone's Send, pinned to the bottom (CSS hides it from 1024px,
          where the strip at the top of the grid takes over). It is the one
          place on the phone that says where sending stands: the button while
          anyone is unsent, then the "Sent to …" line, then the green sent
          line. */}
      {live && rows.length > 0 && (
        <div className="send-bar" role="region" aria-label="Send">
          {unsent.length > 0 ? (
            <>
              <button
                type="button"
                className="btn btn--primary btn--block btn--xl"
                onClick={sendAll}
                disabled={busy === 'send-all' || editing}
              >
                {busy === 'send-all' ? 'Sending…' : sendLabel}
              </button>
              <p
                className={`compose__sendnote${sendNote.tone ? ` compose__sendnote--${sendNote.tone}` : ''}`}
                role="status"
              >
                {editing ? 'Saving your edit…' : sendNote.text}
              </p>
            </>
          ) : (
            <div className="sent-box" role="status">
              {sentMsg ?? sentLine}
            </div>
          )}
        </div>
      )}

      {adding && (
        <ConfirmDialog label="Add staff" onClose={() => setAdding(false)} wide>
          {adding === 'many' ? (
            <PasteStaffForm
              spaceId={space.id}
              variables={space.template_variables ?? []}
              prefill={variablePrefill(space)}
              onDone={(added) => {
                setAdding(false)
                setFlash({
                  type: 'success',
                  text: `${added} ${added === 1 ? 'person' : 'people'} added — not sent yet. Press Send when you're ready.`,
                })
                load()
              }}
              onSingle={() => setAdding('one')}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <AddStaffForm
              spaceId={space.id}
              variables={space.template_variables ?? []}
              prefill={variablePrefill(space)}
              onCreated={(draft) => {
                setAdding(false)
                setSelected(draft.id)
                load()
              }}
              onCancel={() => setAdding(false)}
              onPasteList={() => setAdding('many')}
            />
          )}
        </ConfirmDialog>
      )}
    </main>
  )
}

// Whole days from Toronto's today to a date-only string; negative once past.
function daysLeft(endsAt) {
  const [year, month, day] = String(endsAt).slice(0, 10).split('-').map(Number)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((new Date(year, month - 1, day) - today) / 86_400_000)
}

function initials(row) {
  return `${row.first_name[0] ?? ''}${row.last_name[0] ?? ''}`.toUpperCase()
}

// Where a row stands, as the word at its top right.
const STATUS_TEXT = {
  not_sent: 'Not sent',
  sent: 'Sent',
  opened: 'Opened',
  completed: 'Done',
  revoked: 'Revoked',
  expired: 'Expired',
}

function minutes(secs) {
  return Number.isFinite(secs) ? `${Math.max(1, Math.round(secs / 60))} min` : null
}

// What follows the status word: only how long they talked, once done. No
// times — the word is enough.
function rowMeta(row) {
  if (row.status !== 'completed') return ''
  const lengths = row.calls.map((call) => minutes(call.duration_secs)).filter(Boolean)
  return lengths.length > 0 ? ` · ${lengths.join(', ')}` : ''
}

// The dark welcome band — always there, with the voice agent, and the "what
// happens" strip inside it under the welcome — then, once the engagement has
// ended, a white intro saying so. Nothing here is a button to send: that
// lives under the email.
function Band({ space, first, live, voice }) {
  const welcome = (
    <section className="disc-band disc-band--dark pr-band">
      <div className="pr-band__inner pr-band__inner--split">
        <div className="pr-band__text-col">
          <h2 className="pr-band__title pr-band__title--accent">
            Hi, <span className="pr-band__pyrite">{first}</span>.
          </h2>
          <p className="pr-band__sub">
            Before the <span className="pr-band__pyrite">{space.engagement}</span> lab, we’d
            like to interview your staff on where and how things are currently.
          </p>
        </div>
        {voice}
      </div>
      <section className="what" aria-labelledby="what-title">
        <p id="what-title" className="eyebrow-sm what__eyebrow">
          What happens
        </p>
        {WHAT.map((item, index) => (
          <div key={item.title} className="what__item">
            <span className="what__num" aria-hidden="true">
              {index + 1}
            </span>
            <span className="what__text">
              <span className="what__title">{item.title}</span>
              <span className="what__body">{item.body}</span>
            </span>
          </div>
        ))}
      </section>
    </section>
  )

  // The white intro is for an engagement that has ended. While it is live
  // the list, the bars and the Send button say where things stand.
  let intro = null
  if (!live) {
    intro = (
      <>
        <p className="overline pr-intro__eyebrow">
          {space.archived_at ? 'Closed' : `Ended · ${formatShortDate(space.ends_at)}`}
        </p>
        <h2 className="pr-intro__title pr-intro__title--ink">This engagement has ended.</h2>
        <p className="pr-intro__sub">Thank you, {first}.</p>
        <p className="pr-intro__text">
          The links stopped working on {formatShortDate(space.ends_at)}. The list below shows
          how far each person got.
        </p>
      </>
    )
  }

  return (
    <>
      {welcome}
      {intro && <section className="pr-intro">{intro}</section>}
    </>
  )
}
